import React, {
	type FC,
	useContext,
	useEffect,
	useState,
	useRef,
	useMemo,
	type PropsWithChildren,
} from 'react';
import { Placement } from '@post-office/placements/jira-nin-meatball-menu-button';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import Spinner from '@atlaskit/spinner';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ButtonOld from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';

import DropdownMenu from '@atlaskit/dropdown-menu';
import { Box, xcss } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ErrorState } from '@atlassian/jira-issue-navigator-actions-common/src/ui/error/index.tsx';
import { NinChangeboardingContext } from '@atlassian/jira-issue-navigator-changeboarding/src/controllers/enable-nin-changeboarding/index.tsx';
import type { SearchResultPageData } from '@atlassian/jira-issue-navigator/src/common/types.tsx';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { CoordinationClient } from '@atlassian/jira-engagement/src/ui/coordination-client/index.tsx';
import { useIssueNavigatorFeatures } from '@atlassian/jira-issue-navigator/src/controllers/features-context/index.tsx';
import { useMeatballMenuItems, type RequestPayload } from '../services/meatball-menu/index.tsx';
import { meatballMenuPopUpEntryPoint } from '../entrypoint.tsx';
import messages from './messages.tsx';
import { StatisticsOnboarding } from './statistics-onboarding/index.tsx';
import { useStatisticsSpotlight } from './statistics-onboarding/utils.tsx';
import {
	ONBOARDING_STATISTICS_DISMISS_BUTTON_ID,
	ONBOARDING_STATISTICS_TRY_IT_OUT_BUTTON_ID,
} from './statistics-onboarding/constants.tsx';

export type MeatballMenuProps = {
	jql: string;
	filterId?: string;
	searchResultPageData: SearchResultPageData;
	additionalMenuItems?: React.ReactNode;
	viewOptions?: React.ReactNode;
	isIssueHierarchySupportEnabled?: boolean;
	isHideDoneSupportEnabled?: boolean;
	isHideDoneWorkItemsEnabled?: boolean;
	isConditionalFormattingEnabled?: boolean;
	isConditionalFormattingEditingEnabled?: boolean;
	isGridNavigationEnabled?: boolean;
	showEcosystemAppsInExport?: boolean;
	isModifiedFilter: boolean;
	isInlineFieldConfigEnabled?: boolean;
	onChangeJqlAndGroupBy?: (params: { jql?: string; groupBy?: string }) => void;
};

type StatisticsOnboardingProps = {
	shouldShowSpotlight: boolean;
	onStatisticsTryNow: () => void;
	onStatisticsOnboardingStop: (actionSubject: string) => void;
	coordinationClient: CoordinationClient;
};

const StatisticsOnboardingSpotlight = ({
	children,
	shouldShowSpotlight,
	coordinationClient,
	onStatisticsTryNow,
	onStatisticsOnboardingStop,
}: PropsWithChildren<StatisticsOnboardingProps>) => (
	<StatisticsOnboarding
		shouldShowSpotlight={shouldShowSpotlight}
		coordinationClient={coordinationClient}
		onStatisticsTryNow={onStatisticsTryNow}
		onStatisticsOnboardingStop={() =>
			onStatisticsOnboardingStop(ONBOARDING_STATISTICS_DISMISS_BUTTON_ID)
		}
	>
		{children}
	</StatisticsOnboarding>
);

export const MeatballMenu: FC<MeatballMenuProps> = ({
	jql,
	filterId = '',
	searchResultPageData,
	additionalMenuItems,
	showEcosystemAppsInExport,
	viewOptions,
	isHideDoneSupportEnabled,
	isHideDoneWorkItemsEnabled,
	isModifiedFilter,
	isGridNavigationEnabled,
	isInlineFieldConfigEnabled,
	onChangeJqlAndGroupBy,
}: MeatballMenuProps) => {
	const { data, loading, error, fetch, requestPayload } =
		useMeatballMenuItems(searchResultPageData);
	const { formatMessage } = useIntl();
	const isNinChangeboardingEnabled = useContext(NinChangeboardingContext);
	const isAnonymous = useIsAnonymous();
	const shouldShowOptOut = isNinChangeboardingEnabled && !isAnonymous && !fg('nin_remove_opt_out');

	const [isOpen, setIsOpen] = useState(false);
	const lastUsedRequestPayload = useRef<RequestPayload>();
	const hasPreviousError = useRef(false);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		const isMeatballMenuOpenedWithNewPayload =
			isOpen && JSON.stringify(requestPayload) !== JSON.stringify(lastUsedRequestPayload.current);

		if (!loading && !hasPreviousError.current && isMeatballMenuOpenedWithNewPayload) {
			fetch(requestPayload);
			lastUsedRequestPayload.current = requestPayload;
		}

		// If there was a fetch error, we clear the lastUsedRequestPayload
		if (error) {
			hasPreviousError.current = true;
			lastUsedRequestPayload.current = undefined;
		}
	}, [fetch, isOpen, loading, requestPayload, error]);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		meatballMenuPopUpEntryPoint,
		{},
	);

	const { shouldShowSpotlight, onStatisticsOnboardingStop, coordinationClient } =
		useStatisticsSpotlight();

	const onStatisticsTryNow = () => {
		onStatisticsOnboardingStop(ONBOARDING_STATISTICS_TRY_IT_OUT_BUTTON_ID);
		entryPointActions.load();
		setIsOpen(true);
	};

	const isConditionalFormattingEnabled =
		fg('jira_list_conditional_formatting') &&
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useIssueNavigatorFeatures().isConditionalFormattingEnabled;

	const isConditionalFormattingEditingEnabled =
		fg('saved_list_view_technical_changes') &&
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useIssueNavigatorFeatures().isConditionalFormattingEditingEnabled;

	const runtimeProps = useMemo(
		() => ({
			jql,
			filterId,
			additionalMenuItems,
			viewOptions,
			shouldShowOptOut,
			data,
			showEcosystemAppsInExport,
			isHideDoneWorkItemsEnabled:
				isHideDoneSupportEnabled &&
				isHideDoneWorkItemsEnabled &&
				fg('jsc_bulk_edit_hide_done_work_items'),
			isConditionalFormattingEnabled:
				isConditionalFormattingEnabled && fg('jira_list_conditional_formatting'),
			isConditionalFormattingEditingEnabled:
				isConditionalFormattingEnabled &&
				isConditionalFormattingEditingEnabled &&
				fg('saved_list_view_technical_changes'),
			isModifiedFilter,
			isGridNavigationEnabled: isGridNavigationEnabled && fg('jira_nin_navigation_onboarding'),
			isInlineFieldConfigEnabled:
				isInlineFieldConfigEnabled && fg('jsc_project_scoped_column_config_for_csv_export'),
			onChangeJqlAndGroupBy,
		}),
		[
			jql,
			filterId,
			additionalMenuItems,
			viewOptions,
			shouldShowOptOut,
			data,
			showEcosystemAppsInExport,
			isHideDoneSupportEnabled,
			isHideDoneWorkItemsEnabled,
			isConditionalFormattingEnabled,
			isConditionalFormattingEditingEnabled,
			isModifiedFilter,
			isGridNavigationEnabled,
			isInlineFieldConfigEnabled,
			onChangeJqlAndGroupBy,
		],
	);

	const entryPointTriggerRef = useEntryPointButtonTrigger(entryPointActions, !isOpen);

	const [retryKey, setRetryKey] = useState(0);
	const errorFallback = (onRetry: () => void | Promise<void>) => (
		<ErrorState onRetry={onRetry} pageAction="meatballMenu" />
	);

	return (
		<MaybePlacement>
			<DropdownMenu
				isLoading={
					loading &&
					JSON.stringify(requestPayload) === JSON.stringify(lastUsedRequestPayload.current)
				}
				onOpenChange={(nextState) => {
					if (nextState.isOpen) {
						hasPreviousError.current = false;
					}
					setIsOpen(nextState.isOpen);
					fireUIAnalytics(
						createAnalyticsEvent({
							action: nextState.isOpen ? 'opened' : 'closed',
							actionSubject: 'button',
						}),
						'meatballMenuPopup',
					);
				}}
				isOpen={isOpen}
				trigger={({ triggerRef, ...props }) =>
					isVisualRefreshEnabled() ? (
						<StatisticsOnboardingSpotlight
							shouldShowSpotlight={shouldShowSpotlight}
							onStatisticsTryNow={onStatisticsTryNow}
							onStatisticsOnboardingStop={onStatisticsOnboardingStop}
							coordinationClient={coordinationClient}
						>
							<IconButton
								ref={mergeRefs(triggerRef, entryPointTriggerRef)}
								{...props}
								testId="issue-navigator-action-meatball-menu.ui.menu-trigger"
								isSelected={isOpen}
								label={formatMessage(messages.moreActionsLabel)}
								icon={MoreIcon}
								isTooltipDisabled={false}
							/>
						</StatisticsOnboardingSpotlight>
					) : (
						<StatisticsOnboardingSpotlight
							shouldShowSpotlight={shouldShowSpotlight}
							onStatisticsTryNow={onStatisticsTryNow}
							onStatisticsOnboardingStop={onStatisticsOnboardingStop}
							coordinationClient={coordinationClient}
						>
							<ButtonOld
								{...props}
								testId="issue-navigator-action-meatball-menu.ui.menu-trigger"
								spacing="default"
								isSelected={isOpen}
								iconBefore={
									<MoreIcon label={formatMessage(messages.moreActionsLabel)} spacing="spacious" />
								}
								ref={mergeRefs(triggerRef, entryPointTriggerRef)}
							/>
						</StatisticsOnboardingSpotlight>
					)
				}
				shouldFlip
				placement="bottom-end"
				spacing="compact"
				testId="issue-navigator-action-meatball-menu.ui.dropdown-menu"
			>
				{!error ? (
					<JiraEntryPointContainer
						key={retryKey}
						entryPointReferenceSubject={entryPointReferenceSubject}
						id="popup-content"
						packageName="jiraIssueNavigatorActionMeatballMenu"
						errorFallback={() =>
							errorFallback(() => {
								() => setRetryKey((prev) => prev + 1);
							})
						}
						fallback={
							<Box xcss={spinnerContainerStyles}>
								<Spinner size="small" />
							</Box>
						}
						runtimeProps={runtimeProps}
					/>
				) : (
					errorFallback(() => {
						fetch(requestPayload);
						lastUsedRequestPayload.current = requestPayload;
					})
				)}
			</DropdownMenu>
		</MaybePlacement>
	);
};

const MaybePlacement = ({ children }: PropsWithChildren) => {
	if (!fg('visual-refresh-headers-buttons') || __SERVER__) {
		return <>{children}</>;
	}

	return <Placement>{children}</Placement>;
};

const spinnerContainerStyles = xcss({
	display: 'flex',
	minWidth: '160px',
	padding: 'space.250',
	justifyContent: 'center',
});

export default MeatballMenu;
